import { useCallback, useMemo } from 'react';
import './defs';
import {
  findCommittedCommand,
  isCommandAuthorized,
  matchCommands,
} from './matcher';
import { parseCommand } from './parser';
import { getAllCommands, getCommandById } from './registry';
import type {
  ArgSuggestion,
  CommandDef,
  CommandResult,
  ParsedCommand,
} from './types';
import useCommandContext from './useCommandContext';

/**
 * Shape returned by `useCommandMode`. The host (`GlobalMapSearch.tsx`)
 * uses this to decide which surface to render, what to do on Enter,
 * and — for committed scoped-search commands — how to bridge into
 * the existing search pipeline.
 */
export interface CommandModeState {
  /** Always defined; reflects the current input verbatim. */
  parsed: ParsedCommand;
  /**
   * Commands matching the typed prefix, after the capability filter.
   * Empty array means "no command matches" — the palette renders a
   * lightweight empty state pointing back to the bare-`/` catalog.
   */
  suggestions: CommandDef[];
  /**
   * The command whose id/alias exactly equals the parsed name (after
   * the capability filter). `undefined` while the user is still
   * typing the name.
   */
  committedCommand: CommandDef | undefined;
  /**
   * Set when the committed command is a scoped-search command AND the
   * user has typed at least one whitespace after the name (i.e. moved
   * into the args). Carries the active filter value the host should
   * force on the existing search pipeline.
   */
  scopedFilter: string | undefined;
  /**
   * Verbatim text after the command name, used as the search text
   * when bridging a scoped-search command into the regular pipeline.
   * Empty string when the user has only typed the command name + a
   * space (no query text yet) — the host should treat that as
   * "waiting for input" rather than firing an empty search.
   */
  scopedQuery: string;
  /**
   * True when the palette should swap from rendering the command
   * catalog to rendering live autocomplete rows for the committed
   * action's argument. Driven by:
   *   1. Committed command is `kind: 'action'`.
   *   2. The command def implements `getArgSuggestions`.
   *   3. The user has typed past the command name (trailing space).
   */
  isArgMode: boolean;
  /**
   * Result of the committed action's `getArgSuggestions`. Computed
   * every render while in arg mode; empty in any other state.
   */
  argSuggestions: ArgSuggestion[];
  /**
   * Run a command's action against the user-typed input. Used for
   * Enter on a command suggestion.
   */
  execute: (cmdId: string) => Promise<CommandResult>;
  /**
   * Run a command's action against a synthesized input where `rest`
   * is forced to `argValue`. Used for Enter on a highlighted arg
   * suggestion — the user's typed substring (`/call 88`) becomes the
   * full picked value (`/call 5551234567`) before execute runs.
   */
  executeWithArg: (cmdId: string, argValue: string) => Promise<CommandResult>;
}

/**
 * Resolve the user's slash-command input into actionable state for
 * the host. Uses the side-effect populated registry from `./defs`
 * (the import above is what guarantees the registry is loaded by
 * the time this hook runs).
 */
export default function useCommandMode(input: string): CommandModeState {
  const ctx = useCommandContext();
  const parsed = useMemo(() => parseCommand(input), [input]);

  const allCommands = useMemo(() => getAllCommands(), []);

  const suggestions = useMemo(
    () =>
      matchCommands({
        parsed,
        defs: allCommands,
        capabilities: ctx.capabilities,
      }),
    [parsed, allCommands, ctx.capabilities],
  );

  const committedCommand = useMemo(
    () =>
      findCommittedCommand({
        parsed,
        defs: allCommands,
        capabilities: ctx.capabilities,
      }),
    [parsed, allCommands, ctx.capabilities],
  );

  // The bridge fires only once the user has actually typed past the
  // command name. Without the `trailingSpace` gate, typing `/transcript`
  // with no query would immediately try to run an empty scoped search,
  // which would just disable the trailing search button without showing
  // the user anything useful.
  const scopedFilter =
    committedCommand?.kind === 'scopedSearch' && parsed.trailingSpace
      ? committedCommand.scopedFilter
      : undefined;

  const scopedQuery = scopedFilter ? parsed.rest : '';

  // Arg-suggestion mode kicks in for action commands whose def opts
  // into autocomplete by exporting `getArgSuggestions`. Same
  // `trailingSpace` gate as scoped-search: we want the user to be
  // visibly past the command name before swapping the palette to
  // arg rows, otherwise typing `/cal` would briefly flicker into
  // arg mode at the moment `/call` matched but before any space.
  const isArgMode = Boolean(
    committedCommand?.kind === 'action' &&
      committedCommand.getArgSuggestions &&
      parsed.trailingSpace,
  );

  const argSuggestions = useMemo<ArgSuggestion[]>(() => {
    if (
      !isArgMode ||
      committedCommand?.kind !== 'action' ||
      !committedCommand.getArgSuggestions
    ) {
      return [];
    }
    try {
      return committedCommand.getArgSuggestions(parsed, ctx);
    } catch {
      // A buggy provider shouldn't crash the search bar; treat it
      // the same as "no matches" and let the user keep typing.
      return [];
    }
  }, [isArgMode, committedCommand, parsed, ctx]);

  const execute = useCallback(
    async (cmdId: string): Promise<CommandResult> => {
      const def = getCommandById(cmdId);
      if (!def) {
        return {
          ok: false,
          reason: 'not_found',
          message: `Unknown command: /${cmdId}`,
        };
      }
      if (!isCommandAuthorized(def, ctx.capabilities)) {
        return {
          ok: false,
          reason: 'unauthorized',
          message: `Not available: /${def.id}`,
        };
      }
      // Scoped-search commands have no `execute` — the bridge in the
      // host is what gives them their behavior. Calling `execute` on
      // one is a no-op success so the Enter handler can uniformly
      // close suggestion mode.
      if (def.kind !== 'action') return { ok: true };
      try {
        return await def.execute(parsed, ctx);
      } catch (err) {
        return {
          ok: false,
          reason: 'runtime',
          message: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [ctx, parsed],
  );

  const executeWithArg = useCallback(
    async (cmdId: string, argValue: string): Promise<CommandResult> => {
      const def = getCommandById(cmdId);
      if (!def || def.kind !== 'action') {
        return {
          ok: false,
          reason: 'not_found',
          message: `Cannot execute /${cmdId}`,
        };
      }
      if (!isCommandAuthorized(def, ctx.capabilities)) {
        return {
          ok: false,
          reason: 'unauthorized',
          message: `Not available: /${def.id}`,
        };
      }
      // Synthesize a fresh ParsedCommand whose `rest`/`args` reflect
      // the picked autocomplete value rather than whatever the user
      // had partially typed. The action's `execute` then sees the
      // full value as if the user had typed it themselves before
      // hitting Enter.
      const synthetic: ParsedCommand = {
        raw: `/${cmdId} ${argValue}`,
        name: cmdId,
        args: argValue.length > 0 ? argValue.split(/\s+/) : [],
        rest: argValue,
        trailingSpace: true,
      };
      try {
        return await def.execute(synthetic, ctx);
      } catch (err) {
        return {
          ok: false,
          reason: 'runtime',
          message: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [ctx],
  );

  return {
    parsed,
    suggestions,
    committedCommand,
    scopedFilter,
    scopedQuery,
    isArgMode,
    argSuggestions,
    execute,
    executeWithArg,
  };
}
